// skinIndex.js
// One pass over the skins database, shared by everything that draws an item.
//
// Why this exists
// ---------------
// /all.json is ~40,000 entries. The old picture lookup was three `.find()`
// scans of that array, called once per card inside the render loop. A page
// showing 500 items therefore walked up to 60,000,000 entries every time it
// rendered — which is what made switching tabs freeze the window on an account
// with a real inventory behind it.
//
// The scans are replaced by Maps built once and kept for as long as the
// database array lives. Every lookup below is O(1).
//
// The cache is a WeakMap keyed on the array itself, so it costs nothing when
// the database has not loaded yet, survives every mount and unmount for as long
// as the array is the same one, and is collected with it if it is ever
// replaced. Components no longer need a useMemo of their own to avoid the work.

// Picker spelling -> market_hash_name spelling. The item picker writes "Field
// Tested"; Steam and CSFloat both say "Field-Tested". Defined here rather than
// in helpers.js because the name lookup below is its only real consumer and
// helpers imports from this file, not the other way round. Still re-exported
// from helpers for the modules that have always read it from there.
export const CONDITION_MAP = {
  'Factory New':    'Factory New',
  'Minimal Wear':   'Minimal Wear',
  'Field Tested':   'Field-Tested',
  'Well Worn':      'Well-Worn',
  'Battle Scarred': 'Battle-Scarred',
};

const STICKER_PREFIX = 'Sticker | ';

const cache = new WeakMap();

/**
 * Maps for one skins database.
 *
 *   exact  — "AK-47 | Redline (Field-Tested)" -> image
 *   base   — "AK-47 | Redline"                -> image of its first wear tier
 *   sticker— "shroud | Cologne 2015"          -> image
 *
 * `base` stands in for the old `startsWith(name + ' (')` fallback: an item
 * named without a condition still gets a picture from whichever tier the
 * database lists first. First entry wins in both maps, which is the same one
 * `.find()` used to return.
 */
function buildIndex(skinsData) {
  const exact   = new Map();
  const base    = new Map();
  const sticker = new Map();
  const anyName = new Map();
  const baseCI  = new Map();
  // Every pictured thing in the database, once, under the name a human would
  // type — what searchSkins scans. Wear tiers collapse into one entry because
  // five Redlines in a "pick a picture" list is five copies of one answer.
  const pictured = new Map();

  for (const entry of skinsData) {
    const name = entry?.market_hash_name || entry?.name || '';
    if (!name) continue;
    const image = entry.image || null;

    if (!exact.has(name)) exact.set(name, image);

    // Either spelling of this entry's name, whichever a caller happens to hold.
    // Most sticker rows in the database have a null market_hash_name and are
    // only findable under `name`. Set-if-absent in array order keeps the same
    // entry the old `.find()` would have stopped on.
    for (const alias of [entry.market_hash_name, entry.name]) {
      if (alias && !anyName.has(alias)) anyName.set(alias, image);
    }

    // Case-insensitive, wear suffix removed — what the group-sell picker asks
    // for. Built off `name` because that is the field it matched on.
    if (entry.name) {
      const key = stripWear(entry.name).toLowerCase();
      if (key && !baseCI.has(key)) baseCI.set(key, image);
    }

    // Every prefix that ends just before a " (" — which is exactly the set of
    // names the old `startsWith(skinName + ' (')` scan would have matched. A
    // few entries carry two bracketed parts ("... (Foil) (Cologne 2015)"), so
    // each opening bracket gets its own key rather than only the last one.
    for (let cut = name.indexOf(' ('); cut > 0; cut = name.indexOf(' (', cut + 1)) {
      const stripped = name.slice(0, cut);
      if (!base.has(stripped)) base.set(stripped, image);
    }

    if (image && name.startsWith(STICKER_PREFIX)) {
      const short = name.slice(STICKER_PREFIX.length);
      if (!sticker.has(short)) sticker.set(short, image);
    }

    if (image) {
      const display = stripWear(name);
      if (display && !pictured.has(display)) pictured.set(display, image);
    }
  }

  const search = [...pictured].map(([name, image]) => ({ name, image, lower: name.toLowerCase() }));

  return { exact, base, sticker, anyName, baseCI, search };
}

// The wear suffix, exactly as the two callers' own copies spelled it.
function stripWear(name) {
  return (name || '')
    .replace(/ \((Factory New|Minimal Wear|Field-Tested|Well-Worn|Battle-Scarred)\)$/, '')
    .trim();
}

/** The maps for this database, built on first use and reused after that. */
export function getSkinIndex(skinsData) {
  if (!skinsData?.length) return null;
  let idx = cache.get(skinsData);
  if (!idx) { idx = buildIndex(skinsData); cache.set(skinsData, idx); }
  return idx;
}

/**
 * Picture for a skin name, optionally at a given wear.
 *
 * Same three steps as the scan it replaces, in the same order: the exact
 * market_hash_name at that condition, then the bare name, then any wear tier of
 * it. Returns null rather than a placeholder so callers keep their own empty
 * state.
 */
export function findSkinImage(skinsData, skinName, conditionValue) {
  const idx = getSkinIndex(skinsData);
  if (!idx || !skinName) return null;

  if (conditionValue) {
    const mapped = CONDITION_MAP[conditionValue] || conditionValue;
    const hit = idx.exact.get(`${skinName} (${mapped})`);
    if (hit !== undefined) return hit;
  }
  const hit = idx.exact.get(skinName);
  if (hit !== undefined) return hit;

  const fallback = idx.base.get(skinName);
  return fallback !== undefined ? fallback : null;
}

/**
 * name -> image for stickers.
 *
 * Kept as a named export because callers pass the result to
 * `withStickerImages`. It is now a view onto the shared index rather than a
 * fresh 40,000-entry loop per component, so the seven components that ask for
 * one all get the same Map back.
 */
export function buildStickerIndex(skinsData) {
  return getSkinIndex(skinsData)?.sticker || new Map();
}

/**
 * Picture for a sticker, by either spelling of its name.
 *
 * An image the sticker already carries wins: that one came off the real item,
 * while the database entry is only the catalogue art. Most sticker entries in
 * the database have a null market_hash_name, which is why this looks in
 * `anyName` rather than the market-name map.
 */
export function findStickerImage(skinsData, sticker) {
  if (sticker?.image) return sticker.image;
  const idx = getSkinIndex(skinsData);
  if (!idx || !sticker?.name) return null;
  return idx.anyName.get(sticker.name) || null;
}

/**
 * Picture for a base skin name, ignoring case and wear.
 *
 * The looser sibling of findSkinImage, for the group-sell picker: it holds a
 * name the user typed rather than a market_hash_name, so it cannot rely on
 * exact spelling.
 */
export function findSkinImageLoose(skinsData, skinName) {
  const idx = getSkinIndex(skinsData);
  if (!idx || !skinName) return null;
  return idx.baseCI.get(String(skinName).toLowerCase()) || null;
}

/**
 * Items whose name contains the query, for "pick a picture" searches.
 *
 * Three tiers, because a plain "contains" puts the wrong thing first. "Karambit"
 * matches the Sealed Graffiti before the knife, since the knife is spelled
 * "★ Karambit | Doppler" and the graffiti's name starts earlier in the file:
 *
 *   0  the name starts with the query          "Sticker | …" for "sticker"
 *   1  a part of the name does                 "★ Karambit | Fade" for "karambit"
 *   2  it merely appears somewhere             everything else
 *
 * Tier 1 is what makes the item search usable at all, since almost every weapon
 * name sits behind a ★, a "StatTrak™" or a "Sticker | ".
 *
 * A plain scan rather than a prefix tree: ~28,000 entries, half a millisecond,
 * and it only runs while a search box has focus.
 *
 * @returns {{ name, image }[]} at most `limit` of them
 */
export function searchSkins(skinsData, query, limit = 40) {
  const idx = getSkinIndex(skinsData);
  const q = String(query || '').trim().toLowerCase();
  if (!idx || q.length < 2) return [];

  const hits = [];
  for (const entry of idx.search) {
    const at = entry.lower.indexOf(q);
    if (at === -1) continue;
    const before = at === 0 ? '' : entry.lower[at - 1];
    const tier = at === 0 ? 0 : (before === ' ' || before === '|' ? 1 : 2);
    hits.push({ entry, tier, at });
    // Enough of everything that the rest cannot change the top of the list.
    // Stopping early is what keeps a two-letter query from walking the whole
    // database.
    if (hits.length >= limit * 8) break;
  }

  // Inside a tier, the earlier the query starts the more the name is about it:
  // "★ Karambit | Fade" before "Sealed Graffiti | Karambit (Brick Red)", both
  // of which match "karambit" at a word boundary. Shorter names break the
  // remaining ties, since they are the plainer item.
  hits.sort((a, b) =>
    a.tier - b.tier || a.at - b.at || a.entry.name.length - b.entry.name.length);

  return hits.slice(0, limit).map(({ entry }) => ({ name: entry.name, image: entry.image }));
}
